import { EditIcon, Input, Tooltip } from '@bp/ui-components';
import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import { use_PreviewTeachingLoadQuery } from '../../../../types/planung-graphql-client-defs';
import { LessonFormTeacherType, LessonFormType } from '../../types';

type LessonTeacherTeachingLoadProps = {
  teacher: LessonFormTeacherType;
  index: number;
};

export const LessonTeacherTeachingLoadField = (props: LessonTeacherTeachingLoadProps) => {
  const organizationUuid = useAuthClaims().pimAuthClaims.getOrganizationUuid();
  const currentSchoolYear = useSelectedSchoolYear();
  const { values, setFieldValue } = useFormikContext<LessonFormType>();
  const { t } = useTranslation();

  const allSameType =
    values.lessonUnit?.every((u) => !u.lessonUnitType || u.lessonUnitType === values.lessonUnit![0].lessonUnitType) ??
    true;

  const forceReadOnly = !allSameType || !values.subject || values.teachers?.length === 0;

  const [readonly, setReadonly] = useState(props.teacher.teachingLoadHours == null);
  const [localValue, setLocalValue] = useState(props.teacher.teachingLoadHours?.toString() ?? '');

  //biome-ignore lint/correctness/useExhaustiveDependencies(setFieldValue): stable ref
  useEffect(() => {
    if (forceReadOnly) {
      setReadonly(true);
      setFieldValue(`teachers.${props.index}.teachingLoadHours`, null);
    }
  }, [forceReadOnly, props.index]);

  const [{ data }] = use_PreviewTeachingLoadQuery({
    variables: {
      lessonClasses:
        values.lessonClasses?.map((lc) => ({
          classUuid: lc.class.uuid,
          groups: lc.groups.map((g) => ({
            uuid: g.uuid,
            classFraction: lc.class.groupCount ? 1 / (lc.class.groupCount ?? 1) : undefined,
          })),
        })) ?? [],
      lessonUnits:
        values.lessonUnit?.map((u) => ({
          duration: u.duration != null ? Number(u.duration) : 0,
          count: u.count != null ? Number(u.count) : 0,
          subjectContainerUuid: u.subjectContainerUuid ?? undefined,
        })) ?? [],
      organizationUuid,
      schoolYearUuid: currentSchoolYear?.uuid ?? '',
      subjectUuid: values.subject?.uuid ?? '',
      teacherUuids: values.teachers?.map((t) => t.person?.uuid ?? '') ?? [],
    },
    pause: !organizationUuid || !currentSchoolYear?.uuid || !values.subject?.uuid,
  });

  const previewValue = data?.previewTeachingLoad.reduce((sum, tl) => {
    return tl.teacherUuid === props.teacher.person?.uuid ? sum + tl.deputat : sum;
  }, 0);

  return (
    <Input
      label={
        readonly
          ? t('lesson.person.teachingLoadHours')
          : t('lesson.person.teachingLoadHoursOverwrite', { calculated: previewValue })
      }
      name={`teachers.${props.index}.teachingLoadHours`}
      value={readonly ? String(previewValue ?? '') : localValue}
      suffix={
        forceReadOnly ? undefined : (
          <Tooltip content={t('lesson.person.override')}>
            <EditIcon
              cursor={'pointer'}
              className='small'
              onClick={() => {
                if (readonly) {
                  setLocalValue(String(previewValue ?? ''));
                  setReadonly(false);
                } else {
                  setFieldValue(`teachers.${props.index}.teachingLoadHours`, null);
                  setReadonly(true);
                }
              }}
            />
          </Tooltip>
        )
      }
      readonly={forceReadOnly || readonly}
      onChange={(e) => {
        setLocalValue(e.target.value);
        const parsed = parseFloat(e.target.value.replace(',', '.'));
        setFieldValue(`teachers.${props.index}.teachingLoadHours`, isNaN(parsed) ? null : parsed);
      }}
    />
  );
};
